import React, { MouseEventHandler, useState } from "react";
import { useTranslation } from "react-i18next";
import { ConnectionConfiguration, Destination, Member, Organization } from "../common/ObjectTypes";
import SchemaForm from "../common/SchemaForm";
import SelectInput from "../common/SelectInput";
import { ArrowDown, ArrowRight, AttentionIcon } from "../common/ProjectIcons";
import { checkSchema } from "../common/SchemaFormTemplates/checkSchema";
import { isHiddenUrlDestinationType } from "./isHiddenUrlDestinationType";
import TestAction from "../common/TestAction";

type DestinationDetailsModalConfigProps = {
    currentMemberIsAdmin: boolean,
    destination: Destination,
    organizations: Organization[],
    loadMoreOrgs: Function,
    orgInputChange: Function,
    onChange: Function,
    onChangeSelectedDestinationConfig: Function,
    onTestAction: Function,
    testActionLoading: boolean,
    testActionResponse: any,
    testActionStatus: number,
    onChangeSelectedDestinationOwner: Function,
    onSave: MouseEventHandler,
    onToggleModal: MouseEventHandler,
    errors: any,
    rsjfErrors: any,
}

const DestinationDetailsModalConfig = ({
    currentMemberIsAdmin,
    destination,
    organizations,
    loadMoreOrgs,
    orgInputChange,
    onChange,
    onChangeSelectedDestinationConfig,
    onTestAction,
    testActionLoading,
    testActionResponse,
    testActionStatus,
    onChangeSelectedDestinationOwner,
    onSave,
    onToggleModal,
    errors,
    rsjfErrors,
}: DestinationDetailsModalConfigProps) => {
    const { t } = useTranslation();
    const [activeActionId, setActiveActionId] = useState(null);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const shouldHideUrlField = isHiddenUrlDestinationType(destination.type.name);

    return (
        <div className="h-full flex flex-col">
            <div className="flex flex-col mb-2">
                <label className="text-sm text-secondary-gray dark:text-neutral-gray-400 transition-colors duration-300">{t("Name")} *</label>
                <input
                    id="name"
                    placeholder=""
                    className="mt-2 p-1.5 border border-neutral-gray-200 dark:border-secondary-gray rounded rounded-sm px-2 dark:bg-neutral-gray-700 dark:text-white transition-colors duration-300"
                    value={destination.name ? destination.name : ""}
                    onChange={(e) => onChange(e)}>
                </input>
                {errors.name && <div className="text-orange-600 py-1 rounded flex flex-row items-center"><AttentionIcon /><p className="ml-1 mt-0.5">{errors.name}</p></div>}
            </div>
            <div className="flex flex-col my-2">
                {/* owner */}
                <label className="text-sm text-secondary-gray dark:text-gray-lines">{t("Workspace")} *</label>
                <SelectInput
                    options={organizations.map((org: Organization) => {
                        return {
                            label: org.name,
                            value: org.id
                        }
                    })}
                    className={"basic-single dark:text-white pb-1"}
                    defaultValue={{
                        label: destination.owner.name,
                        value: destination.owner.id
                    }}
                    isDisabled={false}
                    isLoading={false}
                    isClearable={false}
                    isSearchable={true}
                    isMulti={false}
                    name={t("Workspace")}
                    onChange={(e: any) => onChangeSelectedDestinationOwner(e)}
                    onMenuScrollToBottom={loadMoreOrgs}
                    handleInputChange={orgInputChange}
                />
                {errors.owner && <div className="text-orange-600 bg-orange-100 py-1 px-2 rounded"><AttentionIcon /><p className="ml-1 mt-0.5">{errors.owner}</p></div>}
            </div>
            {/* url */}
            {!shouldHideUrlField && <div className="flex flex-col">
                <label className="text-sm text-secondary-gray dark:text-neutral-gray-400 transition-colors duration-300">{t("URL")} *</label>
                <input
                    id="base_url"
                    placeholder=""
                    className="mt-2 p-1.5 border border-neutral-gray-200 dark:border-secondary-gray rounded rounded-sm px-2 dark:bg-neutral-gray-700 dark:text-white transition-colors duration-300"
                    value={destination.base_url ? destination.base_url : ""}
                    onChange={(e) => onChange(e)}>
                </input>
                {errors.base_url && <div className="text-orange-600 py-1 rounded flex flex-row items-center"><AttentionIcon /><p className="ml-1 mt-0.5">{errors.base_url}</p></div>}
            </div>}
            <div className="flex-grow pt-14">
                {/* <label>Configurations</label> */}
                {destination.type.actions.filter((item: any) => item.type !== "pull").map((item: any) => {
                    const uiSchema = item.ui_schema ? { ...item.ui_schema } : {};
                    const formTitle = item.ui_schema && item.ui_schema['ui:title'] ? item.ui_schema['ui:title'] : item.name;
                    return (
                        <div key={item.id} className={checkSchema(item) ? "pb-14" : "hidden"}>
                            {checkSchema(item) && (
                                <>
                                    <div className="flex justify-between items-start w-full mb-4">
                                        <div className="text-lg font-semibold text-secondary-gray dark:text-gray-300 transition-all duration-300">{formTitle}</div>
                                        {item.schema.hasOwnProperty('is_executable') && item.schema.is_executable === true && (
                                            <TestAction
                                                actionId={item.id}
                                                actionValue={item.value}
                                                onTestAction={(id: any, value: any) => {
                                                    onTestAction(id, value);
                                                    setActiveActionId(id);
                                                }}
                                                isLoading={testActionLoading && activeActionId === item.id}
                                                response={activeActionId === item.id ? testActionResponse : null}
                                            />
                                        )}
                                    </div>
                                    <div className="rounded border border-gray-lines dark:border-neutral-gray-600 bg-white dark:bg-dark-gray p-5 mb-8 transition-colors duration-300">
                                        <SchemaForm
                                            userIsAdmin={currentMemberIsAdmin}
                                            formTitle={''}
                                            schema={item.schema}
                                            ui_schema={uiSchema}
                                            formdata={destination.configurations.findIndex((a: ConnectionConfiguration) => a.action.id === item.id) !== -1 ? destination.configurations.find((a: ConnectionConfiguration) => a.action.id === item.id).data : {}}
                                            onChange={(e: any) => onChangeSelectedDestinationConfig(e, item.id)}
                                            isEditable={true}
                                            errors={rsjfErrors[item.id]}
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    )
                })}
            </div>

            {/* advanced configurations */}
            {shouldHideUrlField && <div className="pb-14">
                <div className="mt-6 mb text-lg font-semibold text-secondary-gray dark:text-neutral-gray-400 transition-colors duration-300" onClick={() => setShowAdvanced(!showAdvanced)}>
                    {showAdvanced ? <ArrowDown /> : <ArrowRight />} {t("Advanced Configurations")}
                </div>
                {showAdvanced &&
                    <div className="ml-8 flex flex-col">
                        <label className="text-sm text-secondary-gray dark:text-neutral-gray-400 transition-colors duration-300">{t("URL")}</label>
                        <input
                            id="base_url"
                            placeholder=""
                            className="mt-2 p-1.5 border border-neutral-gray-200 dark:border-secondary-gray rounded rounded-sm px-2 dark:bg-neutral-gray-700 dark:text-white transition-colors duration-300"
                            value={destination.base_url ? destination.base_url : ""}
                            onChange={(e) => onChange(e)}>
                        </input>
                        {errors.base_url && <div className="text-orange-600 py-1 rounded flex flex-row items-center"><AttentionIcon /><p className="ml-1 mt-0.5">{errors.base_url}</p></div>}
                    </div>}
            </div>}
            {/* submit and cancel button */}
            <div className="sticky bottom-0 flex flex-row mt-2 bg-white/50 dark:bg-dark-gray/50 backdrop-blur-sm py-3 transition-colors duration-300">
                <button className="font-bold bg-dark-green hover:bg-status-green text-white font-bold rounded px-7 py-2 transition-colors duration-300"
                    type="submit"
                    onClick={onSave}>
                    {t("Save")}
                </button>
                <button className="font-bold text-medium-gray dark:text-neutral-gray-400 rounded px-7 mx-2 transition-colors duration-300"
                    type="button"
                    onClick={onToggleModal}>
                    {t("Cancel")}
                </button>
            </div>
        </div>

    )
}

export default DestinationDetailsModalConfig;